import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { ConfirmationMessage, ErrorMessage } from "@/components/ui/States";
import { BUSINESS } from "@/lib/constants";

export default function ContactForm() {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [consent, setConsent] = useState(false);
    const [honeypot, setHoneypot] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const startMs = useRef(Date.now());
    useEffect(() => { startMs.current = Date.now(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!name.trim() || !phone.trim() || !message.trim() || !consent) {
            setError("Please share your name, phone, message, and consent.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post("/leads/contact", {
                name: name.trim(),
                phone: phone.trim(),
                email: email.trim() || null,
                message: message.trim(),
                consent_given: true,
                website_url: honeypot,
                start_ms: startMs.current,
            });
            setSuccess(res.data.confirmation_message);
        } catch (err) {
            setError(err?.response?.data?.detail || "That didn't go through. Try again or call us.");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) return <ConfirmationMessage message={success} testId="contact-success" />;

    return (
        <form onSubmit={submit} className="grid gap-4" data-testid="contact-form" noValidate>
            <div>
                <label className="a1-label" htmlFor="cf-name">Your Name <span className="text-[var(--color-danger)]">*</span></label>
                <input id="cf-name" className="a1-input" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" data-testid="cf-name" />
            </div>
            <div>
                <label className="a1-label" htmlFor="cf-phone">Phone <span className="text-[var(--color-danger)]">*</span></label>
                <input id="cf-phone" type="tel" inputMode="tel" className="a1-input" value={phone} onChange={(e) => setPhone(e.target.value)} required autoComplete="tel" data-testid="cf-phone" />
            </div>
            <div>
                <label className="a1-label" htmlFor="cf-email">Email (optional)</label>
                <input id="cf-email" type="email" inputMode="email" className="a1-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="cf-email" />
            </div>
            <div>
                <label className="a1-label" htmlFor="cf-message">Message <span className="text-[var(--color-danger)]">*</span></label>
                <textarea id="cf-message" className="a1-textarea" value={message} onChange={(e) => setMessage(e.target.value)} required data-testid="cf-message" />
            </div>
            <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" required data-testid="cf-consent" />
                <span>By submitting this form, you agree that A1 Screening may contact you by phone, text, or email about your service request. Message and data rates may apply. Message frequency varies. Reply STOP to opt out of texts. See our <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms">Terms</a>.</span>
            </label>
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="a1-honeypot" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} name="website_url" />
            {error ? <ErrorMessage message={error} testId="cf-error" /> : null}
            <button type="submit" className="a1-btn-primary" disabled={submitting} data-testid="cf-submit">
                {submitting ? "Sending…" : "Send Message"}
            </button>
            <p className="a1-helper">Or call <a href={`tel:${BUSINESS.phoneTel}`}>{BUSINESS.phone}</a> · {BUSINESS.hours}.</p>
        </form>
    );
}
